import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Bell, ArrowRight, ShieldCheck, Loader2, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function InviteAccept() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | ready | invalid
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", confirm: "" });

  useEffect(() => {
    api.get(`/invite/${token}`)
      .then((r) => {
        setInvite(r.data);
        setForm((f) => ({ ...f, name: r.data.name || "", email: r.data.email || "", phone: r.data.phone || "" }));
        setState("ready");
      })
      .catch((e) => { setErr(formatApiError(e.response?.data?.detail)); setState("invalid"); });
  }, [token]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.password.length < 6) return setErr("Password must be at least 6 characters");
    if (form.password !== form.confirm) return setErr("Passwords do not match");
    setBusy(true);
    try {
      const { data } = await api.post(`/invite/${token}/accept`, {
        name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(), password: form.password,
      });
      if (data.token) localStorage.setItem("token", data.token);
      window.location.href = "/";
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
      setBusy(false);
    }
  };

  if (state === "loading") {
    return <div className="min-h-screen grid place-items-center bg-[#F9F9F7]"><Loader2 className="w-8 h-8 animate-spin text-[var(--accent)]" /></div>;
  }

  if (state === "invalid") {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F9F9F7] p-6">
        <div data-testid="invite-invalid" className="max-w-sm text-center border border-[var(--line)] bg-white p-10">
          <AlertTriangle className="w-8 h-8 mx-auto text-[var(--danger)]" />
          <h2 className="font-serif text-2xl mt-4">Invite unavailable</h2>
          <p className="text-sm text-[var(--text-2)] mt-2">{err || "This invitation link is no longer valid."}</p>
          <a href="/login" className="mt-6 inline-flex items-center gap-2 h-11 px-5 bg-[#1A1A18] text-white text-sm hover:bg-[var(--accent)] transition-colors">Go to sign in</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#F9F9F7]">
      <div className="relative hidden lg:flex flex-col justify-between p-12 bg-[#2A4B46] overflow-hidden">
        <div className="relative z-10 flex items-center gap-3 text-[#F9F9F7]">
          <div className="w-10 h-10 bg-[#D36B46] flex items-center justify-center"><Bell className="w-5 h-5 text-white" strokeWidth={2.4} /></div>
          <span className="font-mono text-sm tracking-[0.25em] uppercase">RemindMe &amp; Do</span>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-serif text-5xl xl:text-6xl leading-none text-[#F9F9F7] tracking-tight">
            You're joining <span className="text-[#E37B5B] italic">{invite?.business}</span>.
          </h1>
          <p className="mt-6 text-[#A3A39F] leading-relaxed">
            Set up your crew account to see your assigned tasks and log on-site check-ins. Your workspace is private — you'll only see what's assigned to you.
          </p>
        </div>
        <div className="relative z-10 flex gap-2 items-center text-[#A3A39F] font-mono text-xs uppercase tracking-widest">
          <ShieldCheck className="w-4 h-4" /> Secure crew onboarding
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <p className="label-mono text-[var(--text-2)]">Crew onboarding</p>
          <h2 className="font-serif text-4xl mt-2 tracking-tight">Confirm &amp; create password</h2>
          <p className="text-sm text-[var(--text-2)] mt-2">Invited by <span className="font-medium text-[var(--text-1)]">{invite?.business}</span></p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <Field label="Full name" testid="join-name" value={form.name} onChange={set("name")} required />
            <Field label="Email" type="email" testid="join-email" value={form.email} onChange={set("email")} required />
            <Field label="Phone" testid="join-phone" value={form.phone} onChange={set("phone")} placeholder="+1 305 555 0100" />
            <Field label="Create password" type="password" testid="join-password" value={form.password} onChange={set("password")} required />
            <Field label="Confirm password" type="password" testid="join-confirm" value={form.confirm} onChange={set("confirm")} required />

            {err && <div data-testid="join-error" className="text-sm text-[var(--danger)] border border-[var(--danger)]/30 bg-[var(--danger)]/5 px-3 py-2">{err}</div>}

            <button type="submit" disabled={busy} data-testid="join-submit" className="group w-full bg-[#1A1A18] text-[#F9F9F7] h-12 flex items-center justify-center gap-2 font-medium hover:bg-[#D36B46] transition-colors disabled:opacity-50">
              {busy ? "Joining…" : "Join the crew"} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, type = "text", ...rest }) {
  return (
    <label className="block">
      <span className="label-mono text-[var(--text-2)]">{label}</span>
      <input type={type} data-testid={testid} {...rest} className="mt-2 w-full h-12 px-3 border border-[var(--line)] bg-white outline-none focus:border-[#1A1A18] focus:ring-2 focus:ring-[#1A1A18]/10 transition-colors" />
    </label>
  );
}
